import { msg, t } from '@lingui/core/macro';
import { z } from 'zod';
import { pageActionInput, pageSearchPatch } from '@/shared/patterns/list-search';
import { action, can, defineScreen } from '@/shared/screen';
import { MEMBER_PAGE_SIZE, MEMBER_SHEET, USER_GROUP_TABS } from './user-group.search';

// U24: Segurança não é automatizada; as duas telas levam `agent: false` e nenhuma ferramenta é registrada.
export const USER_GROUP_PATH = '/seguranca/grupos';
export const userGroupPath = (groupName: string) => `${USER_GROUP_PATH}/${groupName}`;
export const USER_PATH = '/seguranca/usuarios';

const groupInput = z.object({ id: z.string().min(1), name: z.string().optional() });
const memberInput = z.object({ id: z.string().min(1), name: z.string().optional(), groupName: z.string().min(1) });
const loadApi = () => import('./user-group.api');
const tenantNow = async () => (await import('@/shared/http/client')).getCurrentTenant();

export const userGroupCreate = action({
  id: 'userGroup.create',
  kind: 'draft',
  label: msg`Novo grupo`,
  description: msg`Abre o formulário em branco de grupo de usuários. Nada é gravado até a pessoa salvar.`,
  permission: can('UserGroup', 'Create'),
  run: ({ nav }) => nav.to(userGroupPath('novo')),
});
export const userGroupOpen = action({
  id: 'userGroup.open',
  kind: 'navigate',
  row: true,
  label: msg`Editar`,
  description: msg`Abre a ficha de um grupo de usuários. Nada muda só por abrir.`,
  input: groupInput,
  run: ({ nav, input }) => nav.to(userGroupPath(input.id)),
});
export const userGroupDelete = action({
  id: 'userGroup.delete',
  kind: 'destructive',
  row: true,
  label: msg`Excluir`,
  description: msg`Exclui um grupo de usuários; os membros perdem as permissões que vinham dele. Pede a confirmação da pessoa.`,
  permission: can('UserGroup', 'Delete'),
  input: groupInput,
  // Step-up novo (`GroupListContainer.tsx:58-60` do legado): `GroupWrite` preso ao nome do grupo da rota.
  stepUp: { action: 'GroupWrite', resourceId: (input) => input.id },
  preview: (input) => ({
    title: t`Excluir ${input.name ?? input.id}?`,
    detail: t`Tem certeza que deseja excluir este grupo?`,
    tone: 'destructive',
  }),
  run: async ({ input, stepUp }) => (await loadApi()).deleteUserGroup(input.id, await tenantNow(), stepUp),
});
/** Página com o token do Cognito da página exibida (a tela o guarda para a próxima, G03). */
const tokenPageInput = pageActionInput.extend({ token: z.string().nullable().optional() });
function tokenPageAction(id: string, defaultSize?: number) {
  return action({
    id,
    kind: 'navigate',
    label: msg`Mudar de página`,
    description: msg`Muda a página (começa em 0) ou o tamanho da página da lista que aparece na tela.`,
    input: tokenPageInput,
    run: ({ nav, input }) =>
      nav.search(pageSearchPatch(input, defaultSize === undefined ? {} : { defaultSize })),
  });
}
export const userGroupPage = tokenPageAction('userGroup.list.page');

export const userGroupTab = action({
  id: 'userGroup.tab',
  kind: 'navigate',
  label: msg`Trocar de aba`,
  description: msg`Troca a aba da ficha do grupo (Informações, Membros ou Permissões).`,
  input: z.object({ tab: z.enum(USER_GROUP_TABS) }),
  run: ({ nav, input }) =>
    nav.search({ tab: input.tab === 'informacoes' ? undefined : input.tab, page: undefined, size: undefined }),
});
export const userGroupRefresh = action({
  id: 'userGroup.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo os dados do grupo aberto. Não muda nada.`,
  run: ({ nav }) => nav.search({}),
});
export const userGroupHistory = action({
  id: 'userGroup.history',
  kind: 'navigate',
  label: msg`Histórico`,
  description: msg`Abre o histórico de alterações do grupo. Não muda nada.`,
  run: ({ nav }) => nav.search({}),
});
export const memberAdd = action({
  id: 'userGroup.member.add',
  kind: 'draft',
  label: msg`Novo membro`,
  description: msg`Abre o painel para incluir um operador no grupo. Nada é gravado até a pessoa adicionar.`,
  permission: can('UserGroup', 'Update'),
  run: ({ nav }) => nav.search({ sheet: MEMBER_SHEET }),
});
export const memberOpenUser = action({
  id: 'userGroup.member.openUser',
  kind: 'navigate',
  row: true,
  label: msg`Ver usuário`,
  description: msg`Abre a ficha do operador que é membro do grupo. Nada muda só por abrir.`,
  permission: can('User', 'Read'),
  input: memberInput,
  run: ({ nav, input }) => nav.to(`${USER_PATH}/${input.id}`),
});
export const memberRemove = action({
  id: 'userGroup.member.remove',
  kind: 'destructive',
  row: true,
  label: msg`Excluir`,
  description: msg`Tira um operador do grupo; ele perde as permissões que vinham do grupo. Pede a confirmação da pessoa.`,
  permission: can('UserGroup', 'Delete'),
  input: memberInput,
  // Step-up novo (`MemberTabContainer.tsx:60-62`): `GroupMembership` preso ao operador.
  stepUp: { action: 'GroupMembership', resourceId: (input) => input.id },
  preview: (input) => ({
    title: t`Excluir ${input.name ?? input.id}?`,
    detail: t`Tem certeza que deseja excluir este membro?`,
    tone: 'destructive',
  }),
  run: async ({ input, stepUp }) => (await loadApi()).removeGroupMember(input.groupName, input.id, await tenantNow(), stepUp),
});
export const memberPage = tokenPageAction('userGroup.members.page', MEMBER_PAGE_SIZE);

export const userGroupListScreen = defineScreen({
  id: 'userGroup.list',
  path: USER_GROUP_PATH,
  title: msg`Grupos`,
  description: msg`Grupos de usuários, que reúnem operadores com as mesmas permissões. Tela de Segurança, fora do assistente.`,
  permission: can('UserGroup', 'Read'),
  agent: false,
  place: { icon: 'shield-check', section: 'security' },
  actions: [userGroupCreate, userGroupOpen, userGroupDelete, userGroupPage],
});

export const userGroupDetailScreen = defineScreen({
  id: 'userGroup.detail',
  path: `${USER_GROUP_PATH}/$groupId`,
  title: msg`Grupo`,
  description: msg`Ficha do grupo de usuários: informações, membros e permissões. Tela de Segurança, fora do assistente.`,
  permission: can('UserGroup', 'Read'),
  agent: false,
  place: {
    icon: 'badge-check',
    parent: 'userGroup.list',
    record: msg`Grupo`,
    tabs: [
      { id: 'informacoes', label: msg`Informações` },
      { id: 'membros', label: msg`Membros` },
      { id: 'permissoes', label: msg`Permissões` },
    ],
  },
  actions: [
    userGroupRefresh,
    userGroupHistory,
    userGroupTab,
    memberAdd,
    memberOpenUser,
    memberRemove,
    memberPage,
  ],
});
